import { useState, type FormEvent } from 'react';
import {
  Button,
  ConfirmDialog,
  Input,
  Modal,
  PageHeader,
  RowMenu,
  StatusBadge,
  Switch,
} from '../../components';
import { ApiError } from '../../lib/api';
import { useCrud } from '../../lib/useCrud';

type Department = {
  id: string;
  name: string;
  description: string | null;
  head_user_id: string | null;
  is_active: boolean;
};

type FormState = {
  name: string;
  description: string;
  head_user_id: string;
  is_active: boolean;
};
const toForm = (r?: Department): FormState => ({
  name: r?.name ?? '',
  description: r?.description ?? '',
  head_user_id: r?.head_user_id ?? '',
  is_active: r?.is_active ?? true,
});
const toBody = (f: FormState) => ({
  name: f.name.trim(),
  description: f.description.trim() || null,
  head_user_id: f.head_user_id.trim() || null,
  is_active: f.is_active,
});

export function DepartmentsPage() {
  const crud = useCrud<Department, ReturnType<typeof toBody>, Partial<ReturnType<typeof toBody>>>({
    key: 'hrm-departments',
    path: '/hrm/departments',
    listQuery: 'include_inactive=true&limit=100',
  });
  const [editing, setEditing] = useState<Department | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Department | null>(null);

  return (
    <>
      <PageHeader
        crumb="HRM"
        title="Departments"
        actions={
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
            New department
          </Button>
        }
      />
      {crud.list.isLoading && <div className="card card-body muted">Loading…</div>}
      {crud.list.error && <div className="alert">{(crud.list.error as Error).message}</div>}
      {crud.list.data && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th className="col-num">#</th>
                <th>Name</th>
                <th>Description</th>
                <th>Status</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {crud.list.data.data.map((row, i) => (
                <tr key={row.id}>
                  <td className="col-num">{i + 1}</td>
                  <td>{row.name}</td>
                  <td className="muted">{row.description ?? '—'}</td>
                  <td>
                    <StatusBadge active={row.is_active} />
                  </td>
                  <td>
                    <RowMenu
                      items={[
                        { icon: 'edit', label: 'Edit', onClick: () => setEditing(row) },
                        'divider',
                        {
                          icon: 'trash',
                          label: 'Delete',
                          onClick: () => setDeleting(row),
                          danger: true,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditModal
          row={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSubmit={async (body) => {
            if (editing === 'new') await crud.create.mutateAsync(body);
            else await crud.update.mutateAsync({ id: editing.id, body });
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete department"
        message={deleting ? `Delete "${deleting.name}"?` : null}
        destructive
        confirmLabel="Delete"
        loading={crud.remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await crud.remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </>
  );
}

function EditModal({
  row,
  onClose,
  onSubmit,
}: {
  row?: Department;
  onClose: () => void;
  onSubmit: (b: ReturnType<typeof toBody>) => Promise<void>;
}) {
  const [form, setForm] = useState(() => toForm(row));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(toBody(form));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={row ? 'Edit department' : 'New department'} width={480}>
      <form onSubmit={handle}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            id="dept-name"
            label="Name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
            autoFocus
          />
          <div>
            <label className="field-label" htmlFor="dept-desc">
              Description
            </label>
            <textarea
              id="dept-desc"
              className="input"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <Input
            id="dept-head"
            label="Head user ID (optional)"
            value={form.head_user_id}
            onChange={(v) => setForm({ ...form, head_user_id: v })}
            placeholder="UUID"
          />
          <div className="row">
            <Switch
              on={form.is_active}
              onChange={(v) => setForm({ ...form, is_active: v })}
              label="Active"
            />
            <span className="muted">Active</span>
          </div>
          {error && <div className="alert">{error}</div>}
        </div>
        <div
          className="card-header"
          style={{ justifyContent: 'flex-end', borderTop: '1px solid var(--border)', borderBottom: 'none' }}
        >
          <Button onClick={onClose} disabled={submitting} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
